import React from 'react'
import { Edit, useForm, useSelect } from '@refinedev/antd'
import { Form, Input, Select, DatePicker, TimePicker, InputNumber } from 'antd'
import type { Tables } from '@muaa/shared'
import {
  TIME_FMT,
  DATE_FMT,
  timeValueProps,
  normalizeTime,
  dateLabelValueProps,
  normalizeDateLabel,
} from './create'
import { ImageUploadField } from '../../components/ImageUploadField'

type EventSession = Tables<'event_sessions'>
type Event = Tables<'events'>

const DAY_OPTIONS = [
  { label: 'Friday', value: 'FRIDAY' },
  { label: 'Sabbath', value: 'SABBATH' },
  { label: 'Sunday', value: 'SUNDAY' },
]

export function EventSessionEdit() {
  const { formProps, saveButtonProps, form, query } = useForm<EventSession>()
  const currentImageUrl: string | null = (query?.data?.data?.image_url as string | null) ?? null

  const { selectProps: eventSelectProps } = useSelect<Event>({
    resource: 'events',
    optionLabel: 'title',
    optionValue: 'id',
    pagination: { mode: 'off' },
  })

  // No initialValues transform needed: each temporal Form.Item's getValueProps parses
  // the stored string → dayjs for the picker, and normalize writes it back to the exact
  // stored format — so create + edit share one format-preserving path.
  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Form.Item
          label="Event"
          name="event_id"
          rules={[{ required: true, message: 'Please select an event' }]}
        >
          <Select {...eventSelectProps} placeholder="Select event" />
        </Form.Item>
        <Form.Item label="Title" name="title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Day" name="day">
          <Select options={DAY_OPTIONS} placeholder="Select day" />
        </Form.Item>
        <Form.Item
          label="Date"
          name="date"
          getValueProps={dateLabelValueProps}
          normalize={normalizeDateLabel}
          tooltip="A short day label shown on the session (e.g. 'Sep 4'). The full calendar date lives on the parent Event."
        >
          <DatePicker style={{ width: '100%' }} format={DATE_FMT} />
        </Form.Item>
        <Form.Item
          label="Start Time"
          name="start_time"
          getValueProps={timeValueProps}
          normalize={normalizeTime}
        >
          <TimePicker style={{ width: '100%' }} use12Hours format={TIME_FMT} minuteStep={5} />
        </Form.Item>
        <Form.Item
          label="End Time"
          name="end_time"
          getValueProps={timeValueProps}
          normalize={normalizeTime}
        >
          <TimePicker style={{ width: '100%' }} use12Hours format={TIME_FMT} minuteStep={5} />
        </Form.Item>
        <Form.Item label="Location" name="location">
          <Input />
        </Form.Item>
        <Form.Item label="Address" name="address">
          <Input />
        </Form.Item>
        <Form.Item label="Attire" name="attire">
          <Input />
        </Form.Item>
        <Form.Item label="Going Count" name="going_count">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>
        <ImageUploadField
          form={form}
          name="image_url"
          label="Hero Image URL"
          placeholder="https://…/hero.jpg"
          folder="event_sessions"
          currentUrl={currentImageUrl}
          tooltip="Leave blank to fall back to the placeholder label below"
        />
        <Form.Item label="Image Label" name="image_label">
          <Input />
        </Form.Item>
        <Form.Item label="Sort Order" name="sort_order">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Edit>
  )
}
